import {
  CognitoUserPool,
  CognitoUser,
  CognitoRefreshToken,
  AuthenticationDetails,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';

// Cognito Configuration - Platform Admin pool (with MFA)
// Multi-product + multi-stage: detect product + stage from hostname at runtime.
// Pool IDs per (product, stage). Each product has separate dev + prod pools.
type Stage = 'dev' | 'prod';
type PoolConfig = { UserPoolId: string; ClientId: string; Region: string };
const PRODUCT_COGNITO: Record<string, Record<Stage, PoolConfig>> = {
  cafemaster: {
    dev: { UserPoolId: 'us-east-2_uzrp3S7RQ', ClientId: '679riihl1r3td4k01b8ite7sr0', Region: 'us-east-2' },
    prod: { UserPoolId: 'us-east-2_yfOta1i2s', ClientId: '5rfl1stl8c1bajo97gsfjnripa', Region: 'us-east-2' },
  },
  agenticpos: {
    dev: { UserPoolId: 'us-west-1_aJPgzUvru', ClientId: 'n6htsn4h97mkul6s1dq1j3fe5', Region: 'us-west-1' },
    prod: { UserPoolId: 'us-west-1_o9lUo87fN', ClientId: '6kg889kvicooh4604ne7f7immf', Region: 'us-west-1' },
  },
  // agenticrealestate: pool IDs TBD — add when product launches
};

function detectCognitoConfig(): PoolConfig {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  // Env var override takes priority (localhost dev with custom pool, CI builds)
  const envPool = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
  const envClient = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
  const envRegion = import.meta.env.VITE_COGNITO_REGION || '';
  if (envPool && envClient && envRegion && (hostname === 'localhost' || hostname === '127.0.0.1')) {
    return { UserPoolId: envPool, ClientId: envClient, Region: envRegion };
  }
  // Stage detection: dev. / staging. / stage. = dev pool; everything else = prod pool.
  const stage: Stage = (hostname.includes('dev.') || hostname.includes('staging.') || hostname.includes('stage.'))
    ? 'dev' : 'prod';
  // Localhost defaults to dev since no product-dev prefix in the URL.
  const effectiveStage: Stage = (hostname === 'localhost' || hostname === '127.0.0.1') ? 'dev' : stage;

  if (hostname.includes('rubytill')) return PRODUCT_COGNITO.agenticpos[effectiveStage];
  if (hostname.includes('terrakron') && PRODUCT_COGNITO.agenticrealestate) return PRODUCT_COGNITO.agenticrealestate[effectiveStage];
  // Default to cafemaster (cafemenumaster.com, coffeemasterpro.com, or explicit env)
  if (envPool && envClient && envRegion) return { UserPoolId: envPool, ClientId: envClient, Region: envRegion };
  return PRODUCT_COGNITO.cafemaster[effectiveStage];
}

const COGNITO_CONFIG = detectCognitoConfig();

if (!COGNITO_CONFIG.UserPoolId || !COGNITO_CONFIG.ClientId || !COGNITO_CONFIG.Region) {
  console.error('[Cognito] No pool config resolved for hostname:', typeof window !== 'undefined' ? window.location.hostname : '');
} else {
  console.log(`[Cognito] Using pool ${COGNITO_CONFIG.UserPoolId} (${COGNITO_CONFIG.Region})`);
}

// Initialize Cognito User Pool
const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_CONFIG.UserPoolId,
  ClientId: COGNITO_CONFIG.ClientId
});

/**
 * Complete authentication flow with all possible scenarios:
 * 1. Normal login
 * 2. New password required (first-time login with temporary password)
 * 3. MFA challenges
 * 4. Password change needed
 */
export const cognitoLogin = async (email: string, password: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      // Successful login
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        const accessToken = result.getAccessToken().getJwtToken();
        const refreshToken = result.getRefreshToken().getToken();

        resolve({
          type: 'success',
          idToken,
          accessToken,
          refreshToken,
          user: {
            email,
            name: result.getIdToken().payload['name'] || email.split('@')[0],
            userId: result.getIdToken().payload['sub']
          }
        });
      },

      // User needs to set a new password (first-time login with temporary password)
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        resolve({
          type: 'newPasswordRequired',
          cognitoUser,
          userAttributes,
          requiredAttributes,
          message: 'Please set a new password to complete your first login'
        });
      },

      // MFA challenge
      mfaRequired: (challengeName, challengeParameters) => {
        resolve({
          type: 'mfaRequired',
          cognitoUser,
          challengeName,
          challengeParameters,
          message: 'MFA verification required'
        });
      },

      // SMS MFA
      totpRequired: (challengeName, challengeParameters) => {
        resolve({
          type: 'totpRequired',
          cognitoUser,
          challengeName,
          challengeParameters,
          message: 'Enter your TOTP code'
        });
      },

      // Authentication failed
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

/**
 * Complete new password challenge (when user has temporary password)
 */
export const cognitoCompleteNewPassword = async (
  cognitoUser: CognitoUser,
  newPassword: string,
  requiredAttributes: any = {}
): Promise<any> => {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, requiredAttributes, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        const accessToken = result.getAccessToken().getJwtToken();
        const refreshToken = result.getRefreshToken().getToken();
        const payload = result.getIdToken().payload;
        const email = payload['email'] || cognitoUser.getUsername();

        resolve({
          type: 'success',
          idToken,
          accessToken,
          refreshToken,
          user: {
            email,
            name: payload['name'] || email.split('@')[0],
            userId: payload['sub']
          }
        });
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

/**
 * Handle MFA SMS code verification
 */
export const cognitoSendMFACode = async (
  cognitoUser: CognitoUser,
  mfaCode: string
): Promise<any> => {
  return new Promise((resolve, reject) => {
    cognitoUser.sendMFACode(mfaCode, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        const accessToken = result.getAccessToken().getJwtToken();
        const refreshToken = result.getRefreshToken().getToken();
        const payload = result.getIdToken().payload;
        const email = payload['email'] || cognitoUser.getUsername();

        resolve({
          type: 'success',
          idToken,
          accessToken,
          refreshToken,
          user: {
            email,
            name: payload['name'] || email.split('@')[0],
            userId: payload['sub']
          }
        });
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

/**
 * Send TOTP MFA code
 */
export const cognitoSendTOTPCode = async (
  cognitoUser: CognitoUser,
  totpCode: string
): Promise<any> => {
  return new Promise((resolve, reject) => {
    cognitoUser.sendMFACode(totpCode, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        const accessToken = result.getAccessToken().getJwtToken();
        const refreshToken = result.getRefreshToken().getToken();
        const payload = result.getIdToken().payload;
        const email = payload['email'] || cognitoUser.getUsername();

        resolve({
          type: 'success',
          idToken,
          accessToken,
          refreshToken,
          user: {
            email,
            name: payload['name'] || email.split('@')[0],
            userId: payload['sub']
          }
        });
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

/**
 * Sign up a new user with Cognito
 */
export const cognitoSignUp = async (
  email: string,
  password: string,
  name?: string
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      ...(name ? [new CognitoUserAttribute({ Name: 'name', Value: name })] : []),
    ];

    userPool.signUp(email, password, attributeList, [], (err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve({
          userSub: result?.userSub,
          message: 'Sign up successful. Please check your email for confirmation code.'
        });
      }
    });
  });
};

/**
 * Confirm user registration with verification code
 */
export const cognitoConfirmSignUp = async (email: string, confirmationCode: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve({ message: 'Email confirmed successfully. You can now login.' });
      }
    });
  });
};

/**
 * Resend confirmation code to email
 */
export const cognitoResendConfirmationCode = async (email: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve({ message: 'Confirmation code resent to your email' });
      }
    });
  });
};

/**
 * Forgot password - initiate password reset
 */
export const cognitoForgotPassword = async (email: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    cognitoUser.forgotPassword({
      onSuccess: (result) => {
        resolve({ message: 'Password reset code sent to your email' });
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

/**
 * Confirm forgot password with new password
 */
export const cognitoConfirmPassword = async (
  email: string,
  confirmationCode: string,
  newPassword: string
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });

    cognitoUser.confirmPassword(confirmationCode, newPassword, {
      onSuccess: () => {
        resolve({ message: 'Password reset successfully. Please login with your new password.' });
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
};

/**
 * Change password (for authenticated users)
 */
export const cognitoChangePassword = async (
  oldPassword: string,
  newPassword: string
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No authenticated user found'));
      return;
    }

    cognitoUser.getSession((err: any, session: any) => {
      if (err) {
        reject(err);
      } else if (!session.isValid()) {
        reject(new Error('Session is not valid'));
      } else {
        cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
          if (err) {
            reject(err);
          } else {
            resolve({ message: 'Password changed successfully' });
          }
        });
      }
    });
  });
};

/**
 * Get current authenticated user
 */
export const cognitoGetCurrentUser = (): CognitoUser | null => {
  return userPool.getCurrentUser();
};

/**
 * Get current user session and tokens
 */
export const cognitoGetSession = async (): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No user logged in'));
      return;
    }

    cognitoUser.getSession((err: any, session: any) => {
      if (err) {
        reject(err);
      } else if (!session.isValid()) {
        reject(new Error('Session is not valid'));
      } else {
        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken()
        });
      }
    });
  });
};

/**
 * Refresh JWT tokens using refresh token
 */
export const cognitoRefreshToken = async (refreshToken: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No user logged in'));
      return;
    }

    const token = new CognitoRefreshToken({ RefreshToken: refreshToken });

    cognitoUser.refreshSession(token, (err: any, session: any) => {
      if (err) {
        reject(err);
      } else {
        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken()
        });
      }
    });
  });
};

/**
 * Get user attributes
 */
export const cognitoGetUserAttributes = async (): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No user logged in'));
      return;
    }

    cognitoUser.getUserAttributes((err, attributes) => {
      if (err) {
        reject(err);
      } else {
        resolve(attributes);
      }
    });
  });
};

/**
 * Update user attributes
 */
export const cognitoUpdateUserAttributes = async (attributes: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No user logged in'));
      return;
    }

    // Get session first - required for updateAttributes
    cognitoUser.getSession((sessionErr: Error | null, session: any) => {
      if (sessionErr || !session?.isValid()) {
        reject(sessionErr || new Error('Invalid session'));
        return;
      }

      const attributeList = Object.keys(attributes).map(key => 
        new CognitoUserAttribute({ Name: key, Value: attributes[key] })
      );

      cognitoUser.updateAttributes(attributeList, (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve({ message: 'User attributes updated successfully' });
        }
      });
    });
  });
};

/**
 * Logout user from Cognito
 */
export const cognitoLogout = async (): Promise<void> => {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
};

